import Graphic from "@arcgis/core/Graphic";

/**
 * A transaction team entry as rendered in a list/map view, pairing the full TransactionTeam record with its map graphic (if geolocated), pin marker, and milestone shorthand.
 */
export interface TransactionTeamListItem {
  mapData?: Graphic;
  transactionTeam: TransactionTeam;
  pin: string;
  mileStoneShorthand: string;
}

/**
 * A formed transaction team record: deal terms, location, contacts, cancellation state, and per-role (finance/land/legal/operations) lead assignment and approval status.
 */
export interface TransactionTeam {
  aimTransactionTeam: number;
  aimChecklistMilestone: string;
  teamStatus: string;
  title: string;
  // aimDivision: string | 'PP' | 'C' | 'LP' | 'RN' | 'RC';
  aimAssetType: string;
  aimManagementType: string;
  description: string;
  aimSaleType: string;
  aimTransactionType: string;
  canceledBy: string;
  canceledOn: string;
  cancellationReason: string;
  reasonForCanceling: string | undefined;
  cancellationComment: string | undefined;
  currency: string;
  estPrice: number;
  estAcres: number;
  estCompletion: string;
  bidDate: string;
  country: string;
  stateProvince: string;
  county: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  internalContactName: string;
  jarvisId: string | undefined;
  jarvisName: string | undefined;
  highValueDeal: boolean;
  newCrop: boolean;
  newGeography: boolean;
  financeLead: number;
  finalStatus: string;
  financeLeadActor: string;
  financeLeadName: string;
  financeApproved: false;
  financeApprovedAt: string;
  landLead: number;
  landLeadActor: string;
  landLeadName: string;
  landApproved: boolean;
  landApprovedAt: string;
  legalLead: number;
  legalLeadActor: string;
  legalLeadName: string;
  legalApproved: boolean;
  legalApprovedAt: string;
  operationsLead: number;
  operationsLeadActor: string;
  operationsLeadName: string;
  operationsApproved: boolean;
  operationsApprovedAt: string;
  priority: string;
  minimumDataEntered: boolean;
  readyForApproval: boolean;
  readyToForm: boolean;
  isCanceled: boolean;
  createdBy: string;
  createdOn: string;
  closingDate: string;
  closingAcreage: number;
  closingPrice: number;
}

/**
 * An interface representing the approval details for a transaction team, containing separate boolean flags and timestamps for each approver role.
 *
 * @param operationsApproved - Whether operations has approved. Immutable `false` in the defaults.
 * @param financeApproved - Whether finance has approved. Immutable `false` in the defaults.
 * @param landApproved - Whether land has approved. Immutable `false` in the defaults.
 * @param legalApproved - Whether legal has approved. Immutable `false` in the defaults.
 * @param ceoApproved - Whether the CEO has approved. Immutable `false` in the defaults.
 * @param cfoApproved - Whether the CFO has approved. Immutable `false` in the defaults.
 * @param vpLandApproved - Whether the VP of Land has approved. Immutable `false` in the defaults.
 */
export interface TransactionTeamApprovalDetails {
  operationsApproved: boolean;
  operationsApprovedAt: string | undefined;
  financeApproved: boolean;
  financeApprovedAt: string | undefined;
  landApproved: boolean;
  landApprovedAt: string | undefined;
  legalApproved: boolean;
  legalApprovedAt: string | undefined;
  ceoApproved: boolean;
  ceoApprovedAt: string | undefined;
  cfoApproved: boolean;
  cfoApprovedAt: string | undefined;
  vpLandApproved: boolean;
  vpLandApprovedAt: string | undefined;
}

/**
 * A frozen constant providing default approval values where every `Approved` flag is `false` and every `ApprovedAt` timestamp is `undefined`.
 */
export const teamApprovalDefaults: TransactionTeamApprovalDetails = {
  operationsApproved: false,
  operationsApprovedAt: undefined,
  financeApproved: false,
  financeApprovedAt: undefined,
  landApproved: false,
  landApprovedAt: undefined,
  legalApproved: false,
  legalApprovedAt: undefined,
  ceoApproved: false,
  ceoApprovedAt: undefined,
  cfoApproved: false,
  cfoApprovedAt: undefined,
  vpLandApproved: false,
  vpLandApprovedAt: undefined
};

/**
 * A type representing a pinned tab for quick navigation in the AIM application.
 */
export interface PinnedTab {
  name: string;
  route: string;
  module: AimModules;
}

/**
 * A constant defining module type identifiers for the AIM application.
 */
export const AimModulesType = Object.freeze({
  0: 'OP', // Opportunity
  1: 'AQ', // Acquisition Details
  2: 'TX', // Transaction Team
  3: 'DA' // Detailed Analysis
});

/**
 * Module type identifiers for the AIM application, with numeric codes for Opportunity, Acquisition Details, Transaction Team, and Detailed Analysis modules.
 */
export enum AimModules {
  OP, // Opportunity
  AQ, // Acquisition Details
  TX, // Transaction Team
  DA // Detailed Analysis
}

/**
 * The distinct lead roles that make up a transaction team.
 */
export enum TransactionTeamRoles {
  finance,
  land,
  operations,
  ceo,
  cfo,
  vpLand
}

/**
 * Membership status of a role assignment on a transaction team relative to the most recent change (unchanged, newly added, or removed).
 */
export enum TransactionTeamRoleStatus {
  current,
  added,
  removed
}

/**
 * A type representing a checklist milestone in the transaction lifecycle.
 */
export interface AimChecklistMilestone {
  aimChecklistMilestone: string;
  name: string;
  sequence: number;
  active: boolean;
  modifyTitle: boolean;
  modifyLeads: boolean;
  prismStatusCode: string;
}

/**
 * A constant mapping transaction phases to their status stages.
 */
export interface TransactionStatusStages {
  Canceled: number | string;
  Historic: number | string;
  Draft: number | string;
  Pending: number | string;
  FORMED: number | string;
  PRELIMANALYSIS: number | string;
  RECOMMENDATION: number | string;
  DETAILEDANALYSIS: number | string;
  APPROVALS: number | string;
  NEGOTIATING: number | string;
  DUEDILIGENCE: number | string;
  CLOSING: number | string;
  POSTCLOSING: number | string;
  COMPLETED: number | string;
}

/**
 * An interface representing a single transaction status stage with display properties and sequencing information.
 *
 * @param color - Mantine-compatible color string for the stage indicator.
 * @param name - Human-readable name of the status stage.
 * @param shorthand - Short abbreviation used in map/list views.
 * @param sequence - Order in the transaction lifecycle (zero is historic/canceled, eleven is completed).
 */
export interface TransactionStatusStage {
  color: string;
  name: string;
  shorthand: string;
  sequence: number; // order in the process, zero being historic or canceled
}

/**
 * A constant mapping each transaction status stage to its display color, human-readable name, shorthand, and sequence number in the lifecycle.
 */
export const transactionStatus: Record<keyof TransactionStatusStages | string, TransactionStatusStage> = {
  '': { color: 'grey', name: '', shorthand: '', sequence: 0 },
  Canceled: { color: 'rgba(203,28,37,0.8)', name: 'Canceled', shorthand: 'Canceled', sequence: 0 },
  Rejected: { color: 'rgba(203,28,37,0.8)', name: 'Rejected', shorthand: 'Rejected', sequence: 0 },
  Failed: { color: 'rgba(203,28,37,0.8)', name: 'Failed', shorthand: 'Failed', sequence: 0 },
  Historic: { color: 'rgba(57, 16, 133, 0.8)', name: 'Historic', shorthand: 'Historic', sequence: 0 },
  Draft: { color: 'rgba(8, 151, 156, 0.8)', name: 'Draft', shorthand: 'Draft', sequence: 1 },
  Pending: { color: 'rgba(212, 177, 6, 0.8)', name: 'Pending', shorthand: 'Pending', sequence: 2 },
  FORMED: { color: 'rgba(56, 158, 13, 0.8)', name: 'Formed', shorthand: 'Formed', sequence: 3 },
  PRELIMANALYSIS: {
    color: 'rgba(152,46,80,0.8)',
    name: 'Preliminary Analysis',
    shorthand: 'Analysis',
    sequence: 4
  },
  RECOMMENDATION: {
    color: 'rgba(152,46,80,0.8)',
    // color: 'rgba(73,101,129,0.8)',
    name: 'Recommendation',
    shorthand: 'Analysis',
    sequence: 5
  },
  DETAILEDANALYSIS: {
    color: 'rgba(152,46,80,0.8)',
    // color: 'rgba(104,141,166,0.8)',
    name: 'Detailed Analysis',
    shorthand: 'Analysis',
    sequence: 6
  },
  APPROVALS: { color: 'rgba(171,119,119,0.8)', name: 'Approval', shorthand: 'Approval', sequence: 7 },
  NEGOTIATING: { color: 'rgba(130,95,150,0.8)', name: 'Negotiating', shorthand: 'Negotiating', sequence: 8 },
  DUEDILIGENCE: {
    color: 'rgba(143,154,103,0.8)',
    name: 'Due Diligence',
    shorthand: 'Due Diligence',
    sequence: 9
  },
  CLOSING: {
    color: 'rgba(115,101,101,0.8)',
    name: 'Purchase Approvals & Closing Settlement',
    shorthand: 'Closing',
    sequence: 9
  },
  POSTCLOSING: {
    color: 'rgba(98,122,91,0.8)',
    name: 'Post Closing',
    shorthand: 'Post Closing',
    sequence: 10
  },
  POSTCLOSE: {
    color: 'rgba(98,122,91,0.8)',
    name: 'Post Closing',
    shorthand: 'Post Closing',
    sequence: 10
  },
  COMPLETED: { color: 'rgba(74, 173, 97, 0.8)', name: 'Fully Executed', shorthand: 'Executed', sequence: 11 }
}; // some appropriate extra colors to be used if Statuses don't exist in our transactionStatus dictionary
const colors = [
  'rgba(118,223,15,0.8)',
  'rgb(105,136,157)',
  'rgba(0,10,235,0.8)',
  'rgba(123,10,245,0.8)',
  'rgba(191,10,255,0.8)',
  'rgba(101,88,88,0.65)',
  'rgba(100,77,0,0.8)',
  'rgba(115,133,133,0.8)'
];

/**
 * A constant array of status strings that indicate a transaction is historic (canceled, historic, rejected, failed, declined).
 */
export const isHistoric = ['canceled', 'historic', 'rejected', 'failed', 'declined'];
/**
 * A constant array of status strings that indicate a transaction is fully completed (completed, postclose).
 */
export const isCompleted = ['completed', 'postclose'];

//a hash algorithm to get a predictable color (non-random) index out of our color array based on the status string
function hashString(str: string) {
  let hash = 0;
  for (let i = 0; i < str?.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % colors.length;
}

/**
 * Returns a fixed color string for a given transaction status name, looking it
 * up case-insensitively first, then falling back to a deterministic hash-based
 * color.
 */
export const getTransactionStatusColorByName = (str: string) => {
  const lowerStr = str.toLowerCase();
  const lowerCaseTransactionStatus = Object.keys(transactionStatus).reduce((acc, key) => {
    //@ts-ignore
    acc[key.toLowerCase()] = transactionStatus[key as keyof typeof transactionStatus];
    return acc;
  }, {} as Record<string, { color: string }>);

  if (Object.keys(lowerCaseTransactionStatus).includes(lowerStr)) {
    //@ts-ignore
    return lowerCaseTransactionStatus[lowerStr].color;
  }

  const hashVal = hashString(str);
  return colors[hashVal];
};

/**
 * Returns the shorthand for a transaction team status string, or `undefined` if
 * the key is not found in the status dictionary.
 */
export const getTransactionTeamStatusShorthand = (str: string) => {
  if (Object.keys(transactionStatus).includes(str)) {
    const record = transactionStatus[str as keyof typeof transactionStatus];
    const shorthand = record?.shorthand;
    return shorthand;
  }
};

/**
 * A map from transaction team priority level strings to their display color names.
 */
export const transactionTeamPriorityColorIndex = {
  Low: 'green',
  Medium: 'yellow',
  High: 'red'
};

/**
 * A property summary record from the AIM system, aggregating seller info, location, crops, acreage, pricing, and IRR/CoC metrics.
 */
export interface AimPropertySummary {
  aimPropertySummary: number;
  seller: string;
  location: string;
  crops: string;
  grossAcres: number;
  tillableAcres: number;
  askingPrice: number;
  suggestedOfferPrice: number;
  nearestFRIProperty: string;
  approvedPrice: number;
  pricePerTillableAcre: number;
  irr: number;
  coc: string;
  keyAssumptions: string;
}

/**
 * A changelog entry for an AIM property summary, tracking which field changed, who changed it, and the old/new values over time.
 */
export interface AimPropertySummaryChangelog {
  field: string;
  actor: string;
  oldValue: string | number;
  newValue: string | number;
  timestamp: string;
}

/**
 * Mock transaction team data for development and testing, containing sample records with various transaction states and team compositions.
 */
export const mockTransactionTeams = [
  {
    aimTransactionTeam: 123,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Active',
    title: 'Prime Property Deal',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'Alice Johnson',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 4,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Active',
    title: 'Prime Property Deal 2',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'Alice Johnson',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 5,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Active',
    title: 'Prime Property Deal 3',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'Alice Johnson',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 6,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Active',
    title: 'Prime Property Deal 4',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'John Doe',
    financeApproved: true,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 7,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Active',
    title: 'Prime Property Deal 5',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'Alice Johnson',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 8,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Active',
    title: 'Prime Property Deal 6',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'John Doe',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 9,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Postclose',
    title: 'Prime Property Deal 7',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'John Doe',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 10,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Completed',
    title: 'Prime Property Deal 8',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'John Doe',
    financeApproved: true,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: false,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 11,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Historic',
    title: 'Prime Property Deal 9',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'John Doe',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: true,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  },
  {
    aimTransactionTeam: 12,
    aimChecklistMilestone: 'Initial Review',
    teamStatus: 'Historic',
    title: 'Prime Property Deal 10',
    aimDivision: 'PP',
    description: 'Large commercial land acquisition',
    aimSaleType: 'Sale',
    aimTransactionType: 'Purchase',
    canceledBy: '',
    canceledOn: '',
    cancellationReason: '',
    currency: 'USD',
    estPrice: 1500000,
    estAcres: 10,
    estCompletion: '2024-08-15',
    bidDate: '2024-07-20',
    country: 'United States',
    stateProvince: 'California',
    county: 'Los Angeles',
    contactName: 'John Doe',
    contactPhone: '+1 555-123-4567',
    contactEmail: 'john.doe@example.com',
    internalContactName: 'Jane Smith',
    jarvisId: undefined,
    jarvisName: undefined,
    highValueDeal: true,
    newCrop: false,
    newGeography: true,
    financeLead: 456,
    finalStatus: 'Approved',
    financeLeadActor: 'Finance Manager',
    financeLeadName: 'Alice Johnson',
    financeApproved: false,
    financeApprovedAt: '',
    landLead: 789,
    landLeadActor: 'Land Manager',
    landLeadName: 'Robert Brown',
    landApproved: true,
    landApprovedAt: '2024-07-10',
    legalLead: 101,
    legalLeadActor: 'Legal Counsel',
    legalLeadName: 'Emily White',
    legalApproved: true,
    legalApprovedAt: '2024-07-12',
    operationsLead: 111,
    operationsLeadActor: 'Operations Manager',
    operationsLeadName: 'David Lee',
    operationsApproved: true,
    operationsApprovedAt: '2024-07-15',
    priority: 'High',
    minimumDataEntered: true,
    readyForApproval: true,
    readyToForm: false,
    isCanceled: true,
    createdBy: 'Admin',
    createdOn: '2024-07-01'
  }
];
